import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { CategoryChip, Dialog, EmptyState } from '@/ui/components/bits';
import { ModelAnswer, QuestionPrompts } from '@/ui/components/ModelAnswer';
import {
  abandonExam,
  activeExam,
  formatCountdown,
  msRemaining,
  presetsFor,
  saveAnswer,
  selfGrade,
  startExam,
  submitExam,
  type ExamResult,
} from '@/domain/exam';
import type { ExamRun, Item } from '@/domain/types';

export function ExamScreen() {
  const { items, sessions, local, setExamInProgress, reload } = useAppState();
  const [run, setRun] = useState<ExamRun | null>(null);
  const [result, setResult] = useState<ExamResult | null>(null);
  const [loading, setLoading] = useState(true);

  const presets = presetsFor(local?.examPresets);

  useEffect(() => {
    void activeExam().then((found) => {
      if (found) setRun(found);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setExamInProgress(Boolean(run) && !result);
    return () => setExamInProgress(false);
  }, [run, result, setExamInProgress]);

  const finish = useCallback(
    async (current: ExamRun) => {
      const finished = await submitExam(current, items);
      setResult(finished);
      setRun(null);
      await reload();
    },
    [items, reload],
  );

  if (loading) return <p className="muted">Loading…</p>;

  if (result) {
    return <ExamResults result={result} onDone={() => setResult(null)} />;
  }

  if (run) {
    return <ExamRunner run={run} items={items} onChange={setRun} onFinish={finish} />;
  }

  return (
    <ExamSetup
      presets={presets}
      items={items}
      sessions={sessions}
      onStarted={setRun}
    />
  );
}

function ExamSetup({
  presets,
  items,
  sessions,
  onStarted,
}: {
  presets: ReturnType<typeof presetsFor>;
  items: Item[];
  sessions: ReturnType<typeof useAppState>['sessions'];
  onStarted: (run: ExamRun) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const { toast } = useAppState();

  const available = useMemo(
    () =>
      items.filter(
        (i) => i.type !== 'flashcard' && (selected.length === 0 || selected.includes(i.sessionId)),
      ).length,
    [items, selected],
  );

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (items.filter((i) => i.type !== 'flashcard').length === 0) {
    return (
      <div className="stack">
        <h1>Timed exam</h1>
        <EmptyState icon="⏱" title="No questions yet">
          Import a class with questions first.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="stack">
      <h1>Timed exam</h1>
      <p className="muted" style={{ margin: 0 }}>
        No feedback until the end, and the clock keeps running even if you close the app. The
        periodic table stays available; the Library does not, unless you allow it in Settings.
      </p>

      <h2 className="section-title">Draw from</h2>
      <div className="row">
        <button
          className="btn btn--sm"
          aria-pressed={selected.length === 0}
          onClick={() => setSelected([])}
        >
          Everything
        </button>
        {sessions.map((s) => (
          <button
            key={s.id}
            className="btn btn--sm"
            aria-pressed={selected.includes(s.id)}
            style={
              selected.includes(s.id)
                ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent)' }
                : undefined
            }
            onClick={() => toggle(s.id)}
          >
            {s.shortLabel}
          </button>
        ))}
      </div>
      <p className="faint" style={{ margin: 0 }}>
        {available} question{available === 1 ? '' : 's'} available.
      </p>

      <h2 className="section-title">Length</h2>
      <div className="stack">
        {presets.map((preset) => (
          <button
            key={preset.id}
            className="mode-card"
            disabled={available === 0}
            onClick={async () => {
              const run = await startExam({ preset, items, sessions, sessionIds: selected });
              if (run.itemIds.length === 0) {
                toast('No questions matched that selection.');
                return;
              }
              onStarted(run);
            }}
          >
            <span className="mode-card__title">{preset.label}</span>
            <span className="mode-card__sub">
              {Math.min(preset.questions, available)} questions · {preset.minutes} minutes
              {available < preset.questions ? ' (all that are available)' : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ExamRunner({
  run,
  items,
  onChange,
  onFinish,
}: {
  run: ExamRun;
  items: Item[];
  onChange: (run: ExamRun) => void;
  onFinish: (run: ExamRun) => Promise<void>;
}) {
  const [index, setIndex] = useState(0);
  const [remaining, setRemaining] = useState(() => msRemaining(run));
  const [confirming, setConfirming] = useState(false);
  const submittedRef = useRef(false);

  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const questions = useMemo(
    () => run.itemIds.map((id) => byId.get(id)).filter((i): i is Item => Boolean(i)),
    [run.itemIds, byId],
  );

  const submit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    await onFinish(run);
  }, [onFinish, run]);

  // Wall-clock countdown: re-read the clock every tick rather than counting
  // down a number, so a backgrounded tab cannot gain time.
  useEffect(() => {
    const tick = () => {
      const left = msRemaining(run);
      setRemaining(left);
      if (left === 0) void submit();
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [run, submit]);

  const current = questions[index];
  const answered = current ? run.answers[current.id] : undefined;
  const answeredCount = Object.keys(run.answers).length;

  if (!current) return <p className="muted">This exam has no questions left to show.</p>;

  const low = remaining < 60_000;

  return (
    <div className="stack">
      <div className="spread">
        <h1>Exam</h1>
        <span
          className="mono"
          aria-live="polite"
          style={{
            fontSize: '1.3rem',
            fontWeight: 500,
            color: low ? 'var(--incorrect)' : 'var(--text)',
          }}
        >
          {formatCountdown(remaining)}
        </span>
      </div>

      <div className="progress">
        <div className="progress__fill" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
      </div>
      <div className="counter">
        Question {index + 1} of {questions.length} · {answeredCount} answered
      </div>

      <div className="card stack">
        <CategoryChip category={current.category} />
        <div className="question-text">{current.q}</div>

        {current.type === 'mc' ? (
          <div className="stack">
            {(current.opts ?? []).map((opt, i) => (
              <button
                key={i}
                className="option"
                style={
                  answered?.chosenIndex === i
                    ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
                    : undefined
                }
                onClick={async () => {
                  const next = await saveAnswer(run.id, { itemId: current.id, chosenIndex: i });
                  if (next) onChange(next);
                }}
              >
                <span className="option__key">{String.fromCharCode(65 + i)}</span>
                <span className="option__text">{opt}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <QuestionPrompts item={current} />
            <label className="label" htmlFor="exam-short">
              Your answer
            </label>
            <textarea
              id="exam-short"
              className="input"
              style={{ minHeight: 120, resize: 'vertical' }}
              value={answered?.text ?? ''}
              onChange={async (e) => {
                const next = await saveAnswer(run.id, { itemId: current.id, text: e.target.value });
                if (next) onChange(next);
              }}
            />
            <span className="faint">You will grade this against the sample answer at the end.</span>
          </>
        )}
      </div>

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button className="btn" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
          ‹ Previous
        </button>
        <button
          className="btn"
          disabled={index >= questions.length - 1}
          onClick={() => setIndex((i) => i + 1)}
        >
          Next ›
        </button>
      </div>

      <button className="btn btn--primary btn--block" onClick={() => setConfirming(true)}>
        Submit exam
      </button>

      {confirming ? (
        <Dialog title="Submit now?" onClose={() => setConfirming(false)}>
          <p style={{ margin: 0 }}>
            You have answered {answeredCount} of {questions.length}. Anything unanswered counts as
            wrong.
          </p>
          <div className="row">
            <button className="btn btn--primary" onClick={() => void submit()}>
              Submit
            </button>
            <button className="btn" onClick={() => setConfirming(false)}>
              Keep going
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function ExamResults({ result, onDone }: { result: ExamResult; onDone: () => void }) {
  const { reload } = useAppState();
  const [selfGraded, setSelfGraded] = useState<Record<string, boolean>>({});

  const written = result.needsSelfGrade;
  const outstanding = written.filter((i) => selfGraded[i.id] === undefined).length;

  const selfCorrect = Object.values(selfGraded).filter(Boolean).length;
  const gradedTotal = result.total;
  const totalCorrect = result.correct + selfCorrect;
  const pct = gradedTotal ? Math.round((totalCorrect / gradedTotal) * 100) : 0;

  return (
    <div className="stack">
      <h1>Exam results</h1>

      <div className="card stack">
        <div className="score">
          {totalCorrect} / {gradedTotal}
        </div>
        <div className="counter">{pct}%</div>
        {outstanding > 0 ? (
          <p className="faint" style={{ textAlign: 'center', margin: 0 }}>
            {outstanding} written answer{outstanding === 1 ? '' : 's'} still to grade below.
          </p>
        ) : null}
      </div>

      {written.length > 0 ? (
        <>
          <h2 className="section-title">Grade your written answers</h2>
          <ul className="list">
            {written.map((item) => {
              const typed = result.run.answers[item.id]?.text ?? '';
              const decided = selfGraded[item.id];
              return (
                <li key={item.id} className="card stack">
                  <CategoryChip category={item.category} />
                  <div className="question-text">{item.q}</div>
                  <span className="flashcard__label">What you wrote</span>
                  <div className="sample">{typed || '(nothing)'}</div>
                  <ModelAnswer item={item} />
                  {decided === undefined ? (
                    <div className="row" style={{ justifyContent: 'center' }}>
                      <button
                        className="btn btn--good"
                        onClick={async () => {
                          setSelfGraded((g) => ({ ...g, [item.id]: true }));
                          await selfGrade(result.run.id, item, true);
                          await reload();
                        }}
                      >
                        Got it
                      </button>
                      <button
                        className="btn btn--bad"
                        onClick={async () => {
                          setSelfGraded((g) => ({ ...g, [item.id]: false }));
                          await selfGrade(result.run.id, item, false);
                          await reload();
                        }}
                      >
                        Missed it
                      </button>
                    </div>
                  ) : (
                    <div className={`banner banner--${decided ? 'ok' : 'error'}`}>
                      <strong>{decided ? 'Marked correct' : 'Marked wrong — added to your notebook'}</strong>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      <h2 className="section-title">By topic</h2>
      <ul className="list">
        {result.perCategory.map((c) => (
          <li key={c.category} className="card">
            <div className="spread">
              <CategoryChip category={c.category} />
              <span className="mono">
                {c.correct}/{c.total}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {result.missed.length > 0 ? (
        <>
          <h2 className="section-title">What you missed</h2>
          <ul className="list">
            {result.missed.map((item) => {
              const chosen = result.run.answers[item.id]?.chosenIndex;
              return (
                <li key={item.id} className="card stack">
                  <CategoryChip category={item.category} />
                  <div className="question-text">{item.q}</div>
                  {item.opts ? (
                    <ol style={{ margin: 0, paddingLeft: '1.4em' }}>
                      {item.opts.map((opt, i) => (
                        <li
                          key={i}
                          style={{
                            color:
                              i === item.answerIndex
                                ? 'var(--correct)'
                                : i === chosen
                                  ? 'var(--incorrect)'
                                  : undefined,
                            fontWeight: i === item.answerIndex ? 600 : undefined,
                          }}
                        >
                          {opt}
                          {i === chosen && i !== item.answerIndex ? ' — your answer' : ''}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {item.note ? (
                    <div className="banner banner--info">
                      <strong>Why</strong>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{item.note}</div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p className="faint" style={{ margin: 0 }}>
            Everything you missed is in your mistake notebook.
          </p>
        </>
      ) : null}

      <div className="row">
        <button className="btn btn--primary" onClick={onDone}>
          New exam
        </button>
        <button className="btn" onClick={() => navigate('/notebook')}>
          Mistake notebook
        </button>
        <button className="btn" onClick={() => navigate('/')}>
          Home
        </button>
      </div>
      <button
        className="btn btn--ghost btn--sm"
        onClick={async () => {
          await abandonExam(result.run.id);
          onDone();
        }}
      >
        Discard this attempt from history
      </button>
      <span className="faint">
        Your answers stay on this device only; progress and notebook entries sync as usual.
      </span>
    </div>
  );
}
